import "mapbox-gl/dist/mapbox-gl.css";


import { Button } from "gatsby-theme-material-ui";
import mapboxgl from "mapbox-gl";
import PropTypes from "prop-types";
import React, { ReactElement, useEffect, useRef } from "react";
import ReactDOM from "react-dom";
import styled from "styled-components";
import {
  circle,
  featureCollection,
  point,
  pointsWithinPolygon,
} from "@turf/turf";

import { useTheme } from "@material-ui/core/styles";

import { hasWindow } from "../../util/dom";
import PreviewCompatibleImage from "../PreviewCompatibleImage";
import { getCenterAndZoom } from "./util";



// This wrapper must be positioned relative for the map to be able to lay itself out properly

function getRandomIntInclusive(min, max) {
  min = Math.ceil(min);
  max = Math.floor(max);
  return Math.floor(Math.random() * (max - min + 1)) + min; //Максимум и минимум включаются
}

const Wrapper = styled.div`
  width: ${({ width }): number => width};
  height: ${({ height }): number => height};
  min-height: ${({ minHeight }): number => minHeight};
  position: relative;
  margin: ${({ marginWrap }): number => marginWrap};
  flex: 1 0 auto;
`;

const Map = ({
  width,
  height,
  zoom,
  center,
  bounds,
  padding,
  marginWrap,
  styles,
  sources,
  layers,
  markers,
  showStations,
  minZoom,
  maxZoom,
  onClick,
  minHeight,
}): ReactElement => {
  const mapboxToken = process.env.GATSBY_MAPBOX_TOKEN;

  if (!mapboxToken) {
    console.error(
      "ERROR: Mapbox token is required in GATSBY_MAPBOX_TOKEN environment variable"
    );
  }

  // if there is no window, we cannot render this component
  if (!hasWindow) {
    return null;
  }

  // this ref holds the map DOM node so that we can pass it into Mapbox GL
  const mapNode = useRef(null);

  // this ref holds the map object once we have instantiated it, so that we
  // can use it in other hooks
  const mapRef = useRef(null);

  const theme = useTheme();

  // construct the map within an effect that has no dependencies
  // this allows us to construct it only once at the time the
  // component is constructed.
  useEffect(() => {
    let mapCenter = center;
    let mapZoom = zoom;

    // If bounds are available, use these to establish center and zoom when map first loads
    if (bounds && bounds.length === 4) {
      const { center: boundsCenter, zoom: boundsZoom } = getCenterAndZoom(
        mapNode.current,
        bounds,
        padding
      );
      mapCenter = boundsCenter;
      mapZoom = boundsZoom;
    }

    // Token must be set before constructing map
    mapboxgl.accessToken = mapboxToken;

    const map = new mapboxgl.Map({
      container: mapNode.current,
      style: `mapbox://styles/mapbox/streets-v11`,
      center: mapCenter,
      zoom: mapZoom,
      markers,
      minZoom,
      maxZoom,
      scrollZoom: false,
      antialias: true,
    });
    mapRef.current = map;
    window.map = map; // for easier debugging and querying via console

    map.addControl(new mapboxgl.NavigationControl(), "top-right");



    const createPopup = (marker): mapboxgl.Popup => {
      const placeholder = document.createElement("div");
      placeholder.classList.add("mapboxgl-popup-content-inner");
      const element = (
        <React.Fragment>
          {marker.properties.image && (
            <PreviewCompatibleImage
              imageInfo={{
                childImageSharp: marker.properties.image,
                image: {},
                alt: `featured image thumbnail for post ${marker.properties.title}`,
              }}
            />
          )}
          <footer className="mapboxgl-popup-content-footer">
            <div style={{
              position: "absolute",
              bottom: "5px",
              background: "rgb(220, 0, 78)",
              color: "white",
              padding: "0 8px",
              fontWeight: "bold"
            }}>
              {marker.properties.sold == 2 ? "Sold/STC" : ""}
              {!marker.properties.sold && marker.properties.UO_LA ? "Let Agreed" : ""}
            </div>
            <h2 style={{ marginLeft: "8px", marginRight: "8px" }}>{marker.properties.title}</h2>
            <div>
              <h4>{marker.properties.price}</h4>
              <a href={`../properties/${marker.properties.id}`}>
                <Button>View</Button>
              </a>
            </div>
          </footer>
        </React.Fragment>
      );

      ReactDOM.render(element, placeholder);

      const popup = new mapboxgl.Popup({
        // offset: popupOffsets,
        className: "my-class",
      })
        .setLngLat(marker.geometry.coordinates)
        .setDOMContent(placeholder)
        .setMaxWidth("300px")
        .addTo(map);

      return popup;


    };

    // add layers
    layers.forEach((layer) => {
      map.addLayer(layer);
    });

    map.on("load", () => {
      // add sources
      Object.entries(sources).forEach(([id, source]) => {
        map.addSource(id, source);
      });

      let points;

      if (showStations) {
        map.loadImage("/img/tfl-roundel.png", function (error, image) {
          map.addImage("station", image);

          map.addSource("stations", {
            type: "geojson",
            data: "/geojson/stations.geojson",
          });

          map.addLayer({
            id: "stations",
            type: "symbol",
            source: "stations",
            layout: {
              "icon-image": "station",
              "icon-size": 0.1,
            },
          });

          console.log("stations", map.getSource("stations"));
          // stations.features.forEach((station) => createPopup(station));

          fetch("/geojson/stations.geojson")
            .then((response) => response.json())
            .then((stations) => {
              points = featureCollection(stations.features);
              // console.log(stations.features.length);

              markers.forEach((marker) => {
                const center = point(marker.geometry.coordinates);

                // const nearest = nearestPoint(center, points);

                const radius = 1;
                const options = {
                  steps: 10,
                  units: "kilometers",
                };
                const surroundingArea = circle(center, radius, options);

                const stationsInSurroundingArea = pointsWithinPolygon(
                  points,
                  surroundingArea
                );

                console.log(stationsInSurroundingArea);
                // stationsInSurroundingArea.features.forEach((station) =>
                //   createPopup(station)
                // );

                console.log("marker", marker);
                const popup = createPopup(marker);

                const instance = new mapboxgl.Marker({
                  color: theme.palette.primary.dark,
                })
                  .setLngLat(marker.geometry.coordinates)
                  .setPopup(popup)
                  .addTo(map);
                return instance;
              });
            })
            .catch((error) => console.error(error));
        });
      } else {
        markers.forEach((marker, index) => {
          const popup = createPopup(marker);

          const instance = new mapboxgl.Marker({
            color: `rgb(${
              getRandomIntInclusive(100, 200) +
              getRandomIntInclusive(10, 30) +
              getRandomIntInclusive(0, 10)
              }, ${
              getRandomIntInclusive(100, 200) +
              getRandomIntInclusive(10, 30) +
              getRandomIntInclusive(0, 10)
              }, ${
              getRandomIntInclusive(100, 200) +
              getRandomIntInclusive(10, 30) +
              getRandomIntInclusive(0, 10)
              })`,
          })
            .setLngLat(marker.geometry.coordinates)
            .setPopup(popup)
            .addTo(map);

          return instance;
        });
      }
    });

    // hook up map events here, such as click, mouseenter, mouseleave
    // e.g., map.on('click', (e) => {})
    if (onClick) {
      map.on("click", onClick);
    }

    const mql = window.matchMedia("(prefers-color-scheme: dark)");

    const setStyle = (matches): void => {
      let style = styles[0];

      if (matches) {
        style = styles[1];
      }

      map.setStyle(`mapbox://styles/mapbox/${style}`);
    };

    mql.addListener((e: Event): void => setStyle(e.matches));

    if (mql.matches) {
      setStyle(mql.matches);
    }

    // when this component is destroyed, remove the map
    return (): void => {
      map.remove();
    };
  }, [markers]);

  // You can use other `useEffect` hooks to update the state of the map
  // based on incoming props.  Just beware that you might need to add additional
  // refs to share objects or state between hooks.

  return (
    <Wrapper
      marginWrap={marginWrap}
      width={width}
      height={height}
      minHeight={minHeight}
    >
      <div
        ref={mapNode}
        style={{
          width: "100%",
          height: "100%",
          backgroundColor: theme.palette.grey[100],
        }}
      />
      {/* <StyleSelector map={mapRef.current} styles={styles} token={mapboxToken} /> */}
    </Wrapper>
  );
};

Map.propTypes = {
  width: PropTypes.string,
  height: PropTypes.string,
  minHeight: PropTypes.string,
  center: PropTypes.arrayOf(PropTypes.number),
  zoom: PropTypes.number,
  bounds: PropTypes.arrayOf(PropTypes.number),
  markers: PropTypes.arrayOf(
    PropTypes.shape({
      title: PropTypes.string,
      summary: PropTypes.string,
      image: PropTypes.object,
      coordinates: PropTypes.arrayOf(PropTypes.number),
    })
  ),
  showStations: PropTypes.bool,
  minZoom: PropTypes.number,
  maxZoom: PropTypes.number,
  styles: PropTypes.arrayOf(PropTypes.string),
  padding: PropTypes.number,
  marginWrap: PropTypes.string,
  sources: PropTypes.object,
  layers: PropTypes.arrayOf(PropTypes.object),
  onClick: PropTypes.func,
};

Map.defaultProps = {
  width: "100%",
  height: "100%",
  minHeight: "initial",
  center: [0, 0],
  zoom: 0,
  bounds: null,
  markers: [],
  showStations: false,
  minZoom: 0,
  maxZoom: 24,
  styles: ["light-v9", "dark-v9", "streets-v11"],
  marginWrap: 0,
  padding: 0.1, // padding around bounds as a proportion
  sources: {},
  layers: [],
};

export default React.memo(Map);
